/**
 * PRIORITY CONFIGURATION
 *
 * Simple, centralized configuration for unmet need priorities.
 * No external dependencies to avoid circular imports.
 *
 * Priority order is populated from the API and cached in memory.
 * Defaults fall back to the 5 priority items below.
 */

// Default 5 priority unmet needs (absolute versions)
export const DEFAULT_PRIORITY_ABSOLUTES = [
  "Number of Failure To Escalate With 3 Exacerbations",
  "Number of ICS-LABA Exacerbation Failed Escalation",
  "Number of ICS-LABA Escalation Delay",
  "Number of ICS-LABA Patients with LAMA",
  "Number of Biologic Before Triple",
];

// Default 5 priority unmet needs (percent versions)
export const DEFAULT_PRIORITY_PERCENTS = [
  "Percent of Failure To Escalate With 3 Exacerbations",
  "Percent of ICS-LABA Exacerbation Failed Escalation",
  "Percent of ICS-LABA Escalation Delay",
  "Percent of ICS-LABA Patients with LAMA",
  "Percent of Biologic Before Triple",
];

const normalizeList = (values, fallback) => {
  if (!Array.isArray(values) || values.length === 0) {
    return [...fallback];
  }

  const unique = [];
  const seen = new Set();

  for (const value of values) {
    if (!value || seen.has(value)) {
      continue;
    }

    seen.add(value);
    unique.push(value);
  }

  return unique.length > 0 ? unique : [...fallback];
};

let priorityKeys = {
  absolutes: [...DEFAULT_PRIORITY_ABSOLUTES],
  percents: [...DEFAULT_PRIORITY_PERCENTS],
};

export const setPriorityKeys = ({ absolutes, percents } = {}) => {
  priorityKeys = {
    absolutes: normalizeList(absolutes, DEFAULT_PRIORITY_ABSOLUTES),
    percents: normalizeList(percents, DEFAULT_PRIORITY_PERCENTS),
  };
};

export const getPriorityKeys = () => priorityKeys;

export const getPriorityOrder = (preferPercent = false) => {
  const { absolutes, percents } = priorityKeys;
  return preferPercent ? [...percents, ...absolutes] : [...absolutes, ...percents];
};

export const getDefaultMapPriorityKeys = () =>
  priorityKeys.absolutes.slice(0, 2);

export const getDefaultTablePriorityKeys = () =>
  priorityKeys.percents.slice(0, 2);

/**
 * Sort an array of options to put priority items at the top
 * @param {string[]} options - Array of option keys
 * @param {Object[]} selectedItems - Currently selected items [{value: string}]
 * @returns {string[]} - Sorted array with priorities at top
 */
export function sortWithPrioritiesFirst(
  options,
  selectedItems = [],
  preferPercent = false,
) {
  const selectedSet = new Set((selectedItems || []).map(item => item.value));
  const priorityOrder = getPriorityOrder(preferPercent);
  const prioritySet = new Set(priorityOrder);
  
  // Separate into categories
  const selected = [];
  const priorityNotSelected = [];
  const other = [];
  
  for (const option of options) {
    if (selectedSet.has(option)) {
      selected.push(option);
    } else if (prioritySet.has(option)) {
      priorityNotSelected.push(option);
    } else {
      other.push(option);
    }
  }
  
  // Sort priority items by their order in priorityOrder
  priorityNotSelected.sort((a, b) => 
    priorityOrder.indexOf(a) - priorityOrder.indexOf(b)
  );
  
  // Return: selected first, then priorities, then everything else
  return [...selected, ...priorityNotSelected, ...other];
}

/**
 * Sort an array of column objects to put priority items at the top
 * @param {Object[]} columns - Array of column objects with .id property
 * @param {Object[]} selectedItems - Currently selected items [{value: string}]
 * @returns {Object[]} - Sorted array with priorities at top
 */
export function sortColumnsWithPrioritiesFirst(
  columns,
  selectedItems = [],
  preferPercent = false,
) {
  const selectedSet = new Set((selectedItems || []).map(item => item.value));
  const priorityOrder = getPriorityOrder(preferPercent);
  const prioritySet = new Set(priorityOrder);
  
  const selected = [];
  const priorityNotSelected = [];
  const other = [];
  
  for (const col of columns) {
    if (selectedSet.has(col.id)) {
      selected.push(col);
    } else if (prioritySet.has(col.id)) {
      priorityNotSelected.push(col);
    } else {
      other.push(col);
    }
  }
  
  // Sort priority items by their order in priorityOrder
  priorityNotSelected.sort((a, b) => 
    priorityOrder.indexOf(a.id) - priorityOrder.indexOf(b.id)
  );
  
  return [...selected, ...priorityNotSelected, ...other];
}
